#!/usr/bin/env node

/**
 * hive CLI — shared rooms for AI agents.
 *
 * Usage:
 *   hive [--room <name>] [--port <port>] [--share]                         Host a room + join it
 *   aethereum serve [--room <name>] [--port <port>] [--share]                   Headless server only
 *   aethereum join <url> [--name <name>] [--guest]                              Join a room as a human
 *   aethereum run claude [--name <name>] [--admin] [-- <args>]                Connect Claude Code
 *   aethereum run opencode [--name <name>] [--admin] [-- <args>]                Connect OpenCode
 *   aethereum watch [--url <base>] [--token <key>] [--room <name>]               Live room view (hosted)
 */

import { serve } from "./serve.js";
import { join } from "./join.js";
import { watch } from "./watch/index.js";
import { runMission } from "./mission/index.js";
import { runClaude } from "./claude/run.js";
import { runOpencode } from "./opencode/run.js";
import { runCodex } from "./codex/run.js";
import { buildShareUrl } from "./auth.js";
import { helpBanner, versionLine } from "../hive/help.js";
import { VERSION } from "../version.js";
import { checkForUpdates } from "./update-notifier.js";
import { runMcpStdio } from "../hive/mcp-stdio.js";
import { runHook } from "./hook.js";
import { runInit } from "./init.js";
import { runDemo } from "./demo.js";
import { runStatus } from "./status.js";
import { runStatuslineCommand } from "./statusline.js";
import { runContext } from "./context.js";
import { runChangelog } from "./changelog.js";
import { runCheck } from "./check.js";
import { runGuide } from "./guide.js";
import { runDirective } from "./directive.js";
import { runPrompt } from "./prompt.js";
import { runTicket } from "./ticket.js";
import { runGoal } from "./goal.js";
import { runLogin, runLogout, runWhoami } from "./login.js";
import { runRoom } from "./room.js";
import { runAgent } from "./agent.js";
import { runInvite } from "./invite.js";
import { runClaim } from "./claim.js";
import { runReconnect } from "./reconnect.js";
import { runRotate } from "./rotate.js";
import { runSyncRules } from "./sync-rules.js";
import { runShare, runFetch, runVerify } from "./code.js";
import { runHandoff, runResumeHandoff } from "./handoff.js";
import { runSessions } from "./sessions.js";
import { runUsage } from "./usage.js";
import { runDistill } from "./distill.js";

const args = process.argv.slice(2);

function getFlag(name: string, arr: string[] = args): string | undefined {
  const idx = arr.indexOf(`--${name}`);
  if (idx === -1) return undefined;
  const value = arr[idx + 1];
  if (value === undefined || value.startsWith("--")) return undefined;
  return value;
}

/** Collect all values for a repeatable flag (e.g. --join url1 --join url2). */
function getAllFlags(name: string, arr: string[] = args): string[] {
  const results: string[] = [];
  const flag = `--${name}`;
  for (let i = 0; i < arr.length; i++) {
    if (arr[i] === flag && arr[i + 1] && !arr[i + 1].startsWith("--")) {
      results.push(arr[i + 1]);
    }
  }
  return results;
}

function printUsage(stream: typeof console.log = console.log): void {
  stream(helpBanner());
}

async function main(): Promise<void> {
  // ── --version anywhere ─────────────────────────────────────────────────
  if (args.includes("--version") || args.includes("-v")) {
    console.log(versionLine(process.env.npm_package_version ?? VERSION));
    return;
  }

  // ── --help anywhere ────────────────────────────────────────────────────
  if (args.includes("--help") || args.includes("-h")) {
    printUsage();
    return;
  }

  // ── Update notifier (non-blocking; self-gates to no-op for mcp/hooks/non-TTY,
  //    CI, agent contexts, so it can never corrupt stdio MCP or hang a script) ──
  checkForUpdates({ currentVersion: process.env.npm_package_version ?? VERSION });

  // ── aethereum mcp (stdio MCP server for native claude / `claude mcp add`) ──
  if (args[0] === "mcp") {
    await runMcpStdio({
      join: getFlag("join"),
      server: getFlag("server"),
      token: getFlag("token"),
      name: getFlag("name"),
    });
    return;
  }

  // ── aethereum run <runtime> ───────────────────────────────────────────────
  if (args[0] === "run" && (args[1] === "claude" || args[1] === "opencode" || args[1] === "codex")) {
    const runtime = args[1];
    const restArgs = args.slice(2);

    // Split on -- separator: hive flags before, passthrough args after
    const ddIndex = restArgs.indexOf("--");
    const hiveArgs = ddIndex >= 0 ? restArgs.slice(0, ddIndex) : restArgs;
    const extraArgs = ddIndex >= 0 ? restArgs.slice(ddIndex + 1) : [];

    const joinUrls = getAllFlags("join", hiveArgs);

    const runtimeOptions = {
      joinUrls: joinUrls.length > 0 ? joinUrls : undefined,
      name: getFlag("name", hiveArgs),
      admin: hiveArgs.includes("--admin"),
      headless: hiveArgs.includes("--headless"),
      extraArgs,
    };

    if (runtime === "claude") {
      await runClaude(runtimeOptions);
    } else if (runtime === "opencode") {
      await runOpencode(runtimeOptions);
    } else {
      await runCodex(runtimeOptions);
    }
    return;
  }

  // ── aethereum watch — live ASCII room view (hosted stream) ────────────────
  if (args[0] === "watch") {
    await watch({
      url: getFlag("url"),
      token: getFlag("token"),
      room: getFlag("room"),
    });
    return;
  }

  // ── aethereum mission — interactive cockpit (live room + command bar) ─────
  if (args[0] === "mission") {
    await runMission({
      url: getFlag("url"),
      token: getFlag("token"),
      room: getFlag("room"),
    });
    return;
  }

  // ── aethereum join <url> ──────────────────────────────────────────────────
  if (args[0] === "join") {
    const server = args[1];
    if (!server || server.startsWith("--")) {
      console.error("Usage: aethereum join <code>  (a teammate's join code)  |  aethereum join <url>  (a local room server)");
      process.exit(1);
    }
    await join({
      server,
      name: getFlag("name"),
      guest: args.includes("--guest"),
      headless: args.includes("--headless"),
    });
    return;
  }

  // ── aethereum serve ───────────────────────────────────────────────────────
  if (args[0] === "serve") {
    const portStr = getFlag("port");
    const port = portStr ? parseInt(portStr, 10) : undefined;
    if (port !== undefined && (isNaN(port) || port < 0 || port > 65535)) {
      console.error(`Invalid port: ${portStr}`);
      process.exit(1);
    }
    await serve({
      room: getFlag("room"),
      port,
      share: args.includes("--share"),
      headless: args.includes("--headless"),
      save: getFlag("save"),
      load: getFlag("load"),
    });
    return;
  }

  // ── hive (bare) — host + join ────────────────────────────────────────
  if (args.length === 0 || args[0]?.startsWith("--")) {
    const portStr = getFlag("port");
    const port = portStr ? parseInt(portStr, 10) : undefined;
    if (port !== undefined && (isNaN(port) || port < 0 || port > 65535)) {
      console.error(`Invalid port: ${portStr}`);
      process.exit(1);
    }
    const result = await serve({
      room: getFlag("room"),
      port,
      share: args.includes("--share"),
      quiet: true,
      save: getFlag("save"),
      load: getFlag("load"),
    });

    // Host joins locally as admin using the admin share token
    const adminJoinUrl = buildShareUrl(result.serverUrl, result.adminToken);
    const participantShareUrl = buildShareUrl(
      result.publicUrl !== result.serverUrl ? result.publicUrl : result.serverUrl,
      result.memberToken,
    );

    await join({
      server: adminJoinUrl,
      name: getFlag("name"),
      shareUrl: participantShareUrl,
    });
    return;
  }

  // ── aethereum init ─────────────────────────────────────────────────────────
  if (args[0] === "init") {
    const prd = getFlag("prd");
    const docs = [...(prd ? [prd] : []), ...getAllFlags("doc")];
    await runInit({
      url: getFlag("url"),
      token: getFlag("token"),
      noRoom: args.includes("--no-room"),
      noBrief: args.includes("--no-brief"),
      noLogin: args.includes("--no-login"),
      yes: args.includes("--yes") || args.includes("-y"),
      verbose: args.includes("--verbose"),
      docs: docs.length ? docs : undefined,
      newRoom: args.includes("--new-room"),
    });
    return;
  }

  // ── aethereum reconnect — recover roomId for an older config, NO new room ──
  if (args[0] === "reconnect") {
    await runReconnect({ url: getFlag("url"), token: getFlag("token") });
    return;
  }

  // ── aethereum rotate — rotate the room key + re-wire this project ─────────
  if (args[0] === "rotate") {
    await runRotate({ url: getFlag("url") });
    return;
  }

  // ── aethereum sync-rules — fold the team ruleset into the local AGENTS.md ──
  if (args[0] === "sync-rules") {
    process.exit(await runSyncRules({}));
  }

  // ── aethereum demo — instant two-agent collision walkthrough ──────────────
  if (args[0] === "demo") {
    await runDemo({ url: getFlag("url") });
    return;
  }

  // ── aethereum login / logout / whoami — account token management ──────────
  if (args[0] === "login") {
    const positional = args.slice(1).find((a) => !a.startsWith("--"));
    await runLogin({ token: positional ?? getFlag("token"), url: getFlag("url") });
    return;
  }
  if (args[0] === "logout") {
    await runLogout({});
    return;
  }
  if (args[0] === "whoami") {
    await runWhoami({});
    return;
  }

  // ── aethereum room create [name] | list ───────────────────────────────────
  if (args[0] === "room") {
    await runRoom(args.slice(1), { url: getFlag("url"), token: getFlag("token") });
    return;
  }

  // ── aethereum agent add [name] — second seat in the current room ──────────
  if (args[0] === "agent") {
    await runAgent(args.slice(1), { url: getFlag("url"), token: getFlag("token") });
    return;
  }

  // ── aethereum invite <email> | --link ─────────────────────────────────────
  if (args[0] === "invite") {
    const positional = args.slice(1).find((a) => !a.startsWith("--"));
    await runInvite(positional, { link: args.includes("--link"), url: getFlag("url"), token: getFlag("token") });
    return;
  }

  // ── aethereum claim — attach an anon room to your account ─────────────────
  if (args[0] === "claim") {
    await runClaim({ url: getFlag("url") });
    return;
  }

  // ── aethereum status / aethereum doctor ─────────────────────────────────────────
  if (args[0] === "status" || args[0] === "doctor") {
    await runStatus({ json: args.includes("--json"), fix: args.includes("--fix") });
    return;
  }

  // ── aethereum statusline [install|uninstall] — Claude Code statusline ─────
  if (args[0] === "statusline") {
    process.exit(await runStatuslineCommand(args.slice(1)));
  }

  // ── aethereum context — paste-in shared context for web builders ───────────
  if (args[0] === "context") {
    await runContext({});
    return;
  }

  // ── aethereum changelog — the team's recent notable changes (read-only) ────
  if (args[0] === "changelog") {
    await runChangelog({});
    return;
  }

  // ── aethereum check — the git-boundary contract gate (CAP-1, fail-soft) ────
  if (args[0] === "check") {
    const code = await runCheck({ strict: args.includes("--strict") });
    process.exit(code);
  }

  // ── aethereum guide — how to coordinate in this room (the loop) ────────────
  if (args[0] === "guide") {
    await runGuide({});
    return;
  }

  // ── aethereum directive set "<text>" | clear ─────────────────────────────
  if (args[0] === "directive") {
    await runDirective(args.slice(1), {
      url: getFlag("url"),
      token: getFlag("token"),
    });
    return;
  }

  // ── aethereum share <file> / fetch <ref> — G2 E2EE code sharing ──────────
  if (args[0] === "share") {
    process.exit(await runShare(args.slice(1)));
  }
  if (args[0] === "fetch") {
    process.exit(await runFetch(args.slice(1)));
  }
  if (args[0] === "verify") {
    process.exit(await runVerify(args.slice(1)));
  }

  // ── aethereum handoff / handoff resume — v4-S10 E2EE session handoff ──────
  if (args[0] === "handoff") {
    // v6-S1: `handoff resume` is the subcommand-style alias for `resume-handoff`.
    if (args[1] === "resume") {
      process.exit(await runResumeHandoff(args.slice(2)));
    }
    process.exit(await runHandoff(args.slice(1)));
  }
  if (args[0] === "resume-handoff") {
    process.exit(await runResumeHandoff(args.slice(1)));
  }

  // ── aethereum sessions / usage — local session index + cost report (v4-S9) ─
  if (args[0] === "sessions") {
    process.exit(await runSessions(args.slice(1)));
  }
  if (args[0] === "usage") {
    process.exit(await runUsage(args.slice(1)));
  }

  // ── aethereum distill — skill candidates from your local sessions (v4-S11) ─
  if (args[0] === "distill") {
    process.exit(await runDistill(args.slice(1)));
  }

  // ── aethereum prompt "<text>" [--to <handle>] ────────────────────────────
  if (args[0] === "prompt") {
    // first non-flag arg after the command is the prompt text
    const positional = args.slice(1).find((a) => !a.startsWith("--"));
    await runPrompt(positional ?? "", {
      to: getFlag("to"),
      url: getFlag("url"),
      token: getFlag("token"),
    });
    return;
  }

  // ── aethereum ticket create|list|assign|close|move|doing|done ────────────
  if (args[0] === "ticket") {
    await runTicket(args.slice(1), {
      url: getFlag("url"),
      token: getFlag("token"),
    });
    return;
  }

  // ── aethereum goal set|update|show — drive the loop objective ────────────
  if (args[0] === "goal") {
    await runGoal(args.slice(1), {
      url: getFlag("url"),
      token: getFlag("token"),
    });
    return;
  }

  // ── hive hook <type> — plumbing for Claude Code hooks ─────────────────
  if (args[0] === "hook") {
    const hookType = args[1] as
      | "session-start"
      | "prompt"
      | "stop"
      | "post-tool-use"
      | "subagent-start"
      | "subagent-stop"
      | undefined;
    if (!hookType) {
      // Silent exit — hook is plumbing; bad invocation should not break sessions
      return;
    }
    await runHook(hookType, args.slice(2));
    return;
  }

  // ── Unknown command ────────────────────────────────────────────────────
  console.error(`Unknown command: ${args[0]}\n`);
  printUsage(console.error);
  process.exit(1);
}

main().catch((err) => {
  console.error(err);
  process.exit(1);
});
